/**
 * Registry các module quyền của hệ thống.
 *
 * Cấu trúc:
 *   - PERMISSION_ACTIONS: các action chuẩn dùng chung (view/create/edit/delete)
 *     + các action đặc biệt theo nghiệp vụ (import/export/assign/...).
 *   - PERMISSION_MODULES: danh sách module, mỗi module khai báo các action được phép.
 *   - PERMISSION_GROUPS: gom nhóm module để hiển thị trên UI cho dễ tìm.
 *
 * Cách thêm 1 module mới:
 *   1. (Nếu cần action mới) thêm key vào PERMISSION_ACTIONS.
 *   2. Thêm 1 entry vào PERMISSION_MODULES với key duy nhất, label, actions[].
 *   3. Thêm key module vào PERMISSION_GROUPS để hiển thị đúng group.
 *
 * Permission key được lưu ở dạng `${moduleKey}.${actionKey}`,
 * ví dụ: 'customer.view', 'customer.export', 'appointment.assign'.
 */

export type PermissionActionKey =
    // CRUD chuẩn
    | 'view'
    | 'create'
    | 'edit'
    | 'delete'
    // Action mở rộng (thêm tự do khi nghiệp vụ phát sinh)
    | 'export'
    | 'import'
    | 'assign'
    | 'change_status'
    | 'close'
    | 'approve'
    | 'print'
    | 'config'
    // Chat-specific
    | 'reply'
    | 'tag'
    | 'connect'
    | 'disconnect'
    | 'sync'
    | 'note';

export interface PermissionAction {
    key: PermissionActionKey;
    label: string;
    /** Mô tả ngắn hiển thị tooltip / dưới checkbox */
    description?: string;
}

/** Catalog tất cả action có thể dùng — i18n có thể bọc ngoài khi cần */
export const PERMISSION_ACTIONS: Record<PermissionActionKey, PermissionAction> = {
    view: { key: 'view', label: 'Xem', description: 'Xem danh sách và chi tiết dữ liệu trong module này.' },
    create: { key: 'create', label: 'Thêm', description: 'Tạo mới bản ghi trong module này.' },
    edit: { key: 'edit', label: 'Sửa', description: 'Cập nhật / chỉnh sửa bản ghi hiện có.' },
    delete: { key: 'delete', label: 'Xóa', description: 'Xóa bản ghi (có thể không khôi phục được).' },
    export: { key: 'export', label: 'Xuất Excel', description: 'Tải dữ liệu về dưới dạng Excel/CSV.' },
    import: { key: 'import', label: 'Nhập Excel', description: 'Nhập dữ liệu hàng loạt từ file Excel/CSV.' },
    assign: { key: 'assign', label: 'Phân công', description: 'Gán / chuyển bản ghi cho nhân viên khác.' },
    change_status: { key: 'change_status', label: 'Đổi trạng thái', description: 'Thay đổi trạng thái xử lý của bản ghi.' },
    close: { key: 'close', label: 'Đóng', description: 'Kết thúc / đóng bản ghi, không cho thao tác tiếp.' },
    approve: { key: 'approve', label: 'Duyệt', description: 'Phê duyệt / từ chối yêu cầu trong module.' },
    print: { key: 'print', label: 'In', description: 'In phiếu / chứng từ liên quan.' },
    config: { key: 'config', label: 'Cấu hình', description: 'Cấu hình danh mục, tham số dùng chung của module.' },
    reply: { key: 'reply', label: 'Trả lời', description: 'Gửi tin nhắn, ảnh, file, emoji đến khách.' },
    tag: { key: 'tag', label: 'Gắn nhãn', description: 'Gắn / bỏ nhãn (label, từ khóa chăm sóc).' },
    connect: { key: 'connect', label: 'Kết nối kênh', description: 'Thêm kênh mới (Facebook, Zalo, TikTok, Instagram...).' },
    disconnect: { key: 'disconnect', label: 'Ngắt kết nối', description: 'Xóa / ngắt kết nối / sửa cấu hình kênh đã kết nối.' },
    sync: { key: 'sync', label: 'Đồng bộ', description: 'Đồng bộ dữ liệu từ kênh ngoài về hệ thống.' },
    note: { key: 'note', label: 'Ghi chú', description: 'Thêm ghi chú vào bản ghi.' },
};

/** 4 action CRUD chuẩn, dùng cho hầu hết module */
const CRUD: PermissionActionKey[] = ['view', 'create', 'edit', 'delete'];

export interface PermissionModule {
    key: string;
    label: string;
    description?: string;
    /** Action được phép gán cho module này */
    actions: PermissionActionKey[];
    /**
     * Mô tả riêng cho từng action trong module này, ghí đè mô tả mặc định ở PERMISSION_ACTIONS.
     * Dùng khi ý nghĩa action thay đổi theo nghiệp vụ cụ thể.
     */
    actionDescriptions?: Partial<Record<PermissionActionKey, string>>;
}

/**
 * Danh sách module quyền.
 * Thứ tự trong mảng = thứ tự hiển thị trong group.
 */
export const PERMISSION_MODULES: PermissionModule[] = [
    // --- Tổng quan ---
    {
        key: 'overview',
        label: 'Tổng quan',
        description: 'Trang dashboard tổng quan KPI / số liệu.',
        actions: ['view'],
        actionDescriptions: {
            view: 'Xem trang dashboard tổng quan.',
        },
    },

    // --- CMS / nghiệp vụ chính ---
    {
        key: 'chats',
        label: 'Chats',
        description: 'Hộp thoại chăm sóc khách hàng đa kênh.\n⚠️ Lưu ý: để nhân viên có thể sửa thông tin khách trong chat cần thêm quyền “Khách hàng → Sửa”; để tạo lịch hẹn từ chat cần thêm “Lịch hẹn → Thêm”.',
        actions: ['view', 'reply'],
        actionDescriptions: {
            view: 'Xem danh sách hội thoại và nội dung tin nhắn.',
            reply: 'Trả lời tin nhắn cho khách (text, ảnh, file, emoji, tin nhắn nhanh).',
        },
    },
    {
        key: 'customer',
        label: 'Khách hàng',
        description: 'Danh sách và chi tiết khách hàng.',
        actions: [...CRUD, 'import', 'export'],
        actionDescriptions: {
            edit: 'Cập nhật thông tin khách (tên, SĐT, địa chỉ, ghi chú, nghề nghiệp...). Cũng kiểm soát form chỉnh sửa khách hàng trong giao diện Chat.',
            import: 'Nhập danh sách khách hàng từ file Excel.',
            export: 'Xuất danh sách khách hàng ra Excel.',
        },
    },
    {
        key: 'appointment',
        label: 'Lịch hẹn',
        description: 'Lịch hẹn dịch vụ của khách hàng.',
        actions: ['view', 'create', 'edit', 'export', 'import', 'note'],
        actionDescriptions: {
            create: 'Tạo lịch hẹn mới. Cũng kiểm soát nút “Tạo lịch hẹn” và panel “Dịch vụ quan tâm” trong giao diện Chat.',
            note: 'Thêm ghi chú vào lịch hẹn.',
        },
    },
    {
        key: 'consultation',
        label: 'Lịch tư vấn',
        description:
            'Lịch hẹn tư vấn chuyên môn tại cơ sở.\n⚠️ Lưu ý: quyền “Sửa” và “Đổi trạng thái” chỉ áp dụng cho lịch mà nhân viên ĐƯỢC PHÂN CÔNG (trực page / telesale / sale cơ sở / thực hiện); lịch của người khác vẫn chỉ xem được.',
        actions: ['view', 'edit', 'change_status', 'note'],
        actionDescriptions: {
            edit: 'Cập nhật dữ liệu trong chi tiết lịch tư vấn (thông tin khách, dịch vụ, lịch hẹn...). Chỉ với lịch được phân công cho mình.',
            change_status: 'Đổi trạng thái buổi tư vấn. Chỉ với lịch được phân công cho mình. Quyền “Sửa” đã bao hàm quyền này.',
            note: 'Thêm ghi chú vào lịch tư vấn.',
        },
    },
    {
        key: 'execution',
        label: 'Lịch thực hiện',
        description: 'Lịch thực hiện dịch vụ sau khi tư vấn.',
        actions: ['view', 'edit', 'note'],
        actionDescriptions: {
            note: 'Thêm ghi chú vào lịch thực hiện.',
        },
    },
    {
        key: 'checkin',
        label: 'Màn check-in',
        description:
            'Máy check-in tại sảnh: khách nhập SĐT / quét QR để check-in, đăng ký khách mới và đặt lịch.\nNhóm quyền chỉ có quyền này thì đăng nhập vào thẳng màn check-in và không mở được màn nào khác.',
        actions: ['view'],
        actionDescriptions: {
            view: 'Mở và sử dụng màn check-in.',
        },
    },
    {
        key: 'list_order',
        label: 'Danh sách bán hàng',
        description: 'Đơn hàng / phiếu bán.',
        actions: [...CRUD, 'import', 'export'],
    },

    // --- Chuyên môn ---
    { key: 'hair_clinic', label: 'Phòng khám tóc', description: 'Phiếu chuyên môn da – tóc.', actions: CRUD },
    { key: 'miko_reception', label: 'Tiếp nhận Miko', description: 'Phiếu tiếp nhận Miko.', actions: CRUD },
    { key: 'cosmetic_surgery', label: 'Phiếu phẫu thuật', description: 'Phiếu phẫu thuật thẩm mỹ.', actions: CRUD },
    { key: 'surgery', label: 'Phòng mổ & ê-kíp', description: 'Lên lịch mổ, phòng mổ, lịch trình triển khai, điều động ê-kíp.', actions: CRUD },
    { key: 'thread_lift', label: 'Phiếu căng chỉ', description: 'Phiếu căng chỉ collagen.', actions: CRUD },
    { key: 'ozempic', label: 'Phiếu Ozempic', description: 'Phiếu điều trị Ozempic.', actions: CRUD },
    { key: 'beauty_consultation', label: 'Tư vấn (mặt)', description: 'Phiếu tư vấn thẩm mỹ vùng mặt.', actions: CRUD },
    { key: 'skin_consultation', label: 'Tư vấn da', description: 'Phiếu tư vấn điều trị da.', actions: CRUD },

    // --- Danh mục ---
    { key: 'sale_tags', label: 'Tag bán hàng', description: 'Nhãn phân loại khách theo sale.', actions: CRUD },
    { key: 'chat_tags', label: 'Tag chat', description: 'Nhãn trạng thái chăm sóc hội thoại.', actions: CRUD },
    { key: 'profession', label: 'Nghề nghiệp', description: 'Danh mục nghề nghiệp của khách.', actions: CRUD },
    { key: 'quick_messages', label: 'Tin nhắn nhanh', description: 'Mẫu tin nhắn dùng nhanh khi chat.', actions: CRUD },

    // --- Nhân viên ---
    {
        key: 'employee',
        label: 'Nhân viên',
        description: 'Quản lý tài khoản nhân viên.',
        actions: [...CRUD, 'import', 'export'],
    },
    { key: 'roles', label: 'Phân quyền', description: 'Bảng quyền chi tiết (cho phép customize).', actions: CRUD },
    {
        key: 'group_roles',
        label: 'Nhóm quyền',
        description: 'Nhóm quyền gán cho nhân viên.',
        actions: [...CRUD, 'assign'],
        actionDescriptions: {
            assign: 'Gán nhóm quyền cho nhân viên.',
        },
    },
    { key: 'departments', label: 'Phòng ban', description: 'Cơ cấu phòng ban / chi nhánh.', actions: CRUD },

    // --- Dịch vụ & Sản phẩm ---
    { key: 'service_categories', label: 'Danh mục dịch vụ', description: 'Nhóm / danh mục dịch vụ.', actions: CRUD },
    { key: 'services', label: 'Dịch vụ', description: 'Danh mục dịch vụ bán cho khách.', actions: [...CRUD, 'import', 'export'] },
    { key: 'product_categories', label: 'Danh mục sản phẩm', description: 'Nhóm / danh mục sản phẩm.', actions: CRUD },
    { key: 'products', label: 'Sản phẩm', description: 'Sản phẩm bán / mỹ phẩm.', actions: [...CRUD, 'import', 'export'] },

    // --- Khác ---
    {
        key: 'advertise',
        label: 'Quảng cáo',
        description: 'Chiến dịch quảng cáo.',
        actions: [...CRUD, 'approve'],
        actionDescriptions: {
            approve: 'Duyệt / từ chối chiến dịch quảng cáo.',
        },
    },
    { key: 'branch_manager', label: 'Quản lý chi nhánh', description: 'Quản lý cơ sở / chi nhánh.', actions: CRUD },
    {
        key: 'pos',
        label: 'Bán hàng POS',
        description: 'Bán hàng tại quầy, đơn hàng, đơn nháp, giao dịch, hoàn tiền, khuyến mãi, import MySpa, thống kê.',
        actions: [...CRUD, 'approve', 'import', 'export', 'print'],
        actionDescriptions: {
            view: 'Xem đơn hàng, giao dịch, phiếu hoàn, khuyến mãi và thống kê.',
            create: 'Bán hàng tại quầy, lưu nháp, thu nợ, tạo phiếu hoàn và chi tiền hoàn.',
            edit: 'Thêm / sửa / tắt chương trình khuyến mãi, voucher và hạn mức chiết khấu.',
            delete: 'Hủy đơn chưa thu tiền, xóa / mở khóa đơn nháp của người khác.',
            approve: 'Duyệt chiết khấu vượt hạn mức (PIN) và duyệt phiếu hoàn tiền.',
            import: 'Import đơn hàng từ MySpa và hoàn tác lô import.',
            export: 'Xuất Excel đơn hàng, giao dịch, thống kê.',
            print: 'In / in lại hóa đơn K80, A4.',
        },
    },
    {
        key: 'treatment',
        label: 'Thẻ liệu trình',
        description: 'Gói liệu trình, thẻ của khách, trừ buổi, đặt lịch từ thẻ, thanh toán nhiều đợt, công nợ và hoàn tiền thẻ.',
        actions: [...CRUD, 'approve', 'export'],
        actionDescriptions: {
            view: 'Xem tổng quan, danh sách thẻ, công nợ, gói liệu trình.',
            create: 'Bán thẻ, trừ buổi, check-in lịch hẹn, đặt lịch, thu tiền đợt, tạo phiếu hoàn.',
            edit: 'Tạo / sửa gói, quy tắc thẻ; gia hạn, điều chỉnh buổi, sửa lịch thanh toán.',
            delete: 'Hủy lượt trừ buổi của người khác.',
            approve: 'Duyệt trừ buổi vượt quy tắc (nợ quá hạn, dùng vượt tiền đã đóng) và duyệt hoàn tiền thẻ.',
            export: 'Xuất Excel thẻ, công nợ.',
        },
    },
    {
        key: 'document',
        label: 'Tài liệu',
        description: 'Tài liệu nội bộ: tạo, chia sẻ, duyệt ban hành, thu hồi, thùng rác, loại & danh mục.',
        actions: [...CRUD, 'approve', 'export', 'config'],
        actionDescriptions: {
            view: 'Xem tài liệu cá nhân và tài liệu được chia sẻ, góp ý dự thảo.',
            create: 'Tạo tài liệu, sửa / gửi duyệt tài liệu của mình, ban hành lại.',
            edit: 'Màn Quản lý tài liệu: xem và sửa tài liệu của mọi người.',
            delete: 'Xóa tài liệu vào thùng rác, khôi phục và xóa vĩnh viễn.',
            approve: 'Duyệt / từ chối, ban hành và thu hồi tài liệu.',
            export: 'Xuất Excel danh sách tài liệu.',
            config: 'Cấu hình loại tài liệu và danh mục tài liệu.',
        },
    },

    // --- Marketing ---
    {
        key: 'marketing',
        label: 'BCCD Marketing',
        description: 'Báo cáo Marketing, báo cáo hiệu suất chiến dịch, tài khoản quảng cáo, tiền tố chiến dịch.',
        actions: ['view', 'export', 'config'],
        actionDescriptions: {
            config: 'Thêm / sửa / xóa tiền tố chiến dịch.',
        },
    },

    // --- Workplace ---
    {
        key: 'social',
        label: 'Mạng nội bộ',
        description: 'Bảng tin truyền thông nội bộ.',
        actions: CRUD,
        actionDescriptions: {
            create: 'Đăng bài viết mới.',
            edit: 'Sửa bài viết của người khác.',
            delete: 'Xóa bài viết của người khác.',
        },
    },
    {
        key: 'task',
        label: 'Công việc',
        description: 'Báo cáo công việc, việc lặp, lịch làm việc / cuộc họp.',
        actions: [...CRUD, 'assign', 'approve'],
        actionDescriptions: {
            assign: 'Giao việc cho nhân viên khác.',
            approve: 'Phê duyệt đề xuất / kết quả công việc.',
        },
    },
    {
        key: 'project',
        label: 'Dự án',
        description: 'Danh sách và chi tiết dự án.',
        actions: [...CRUD, 'config'],
        actionDescriptions: {
            config: 'Cài đặt dự án.',
        },
    },
    {
        key: 'signature',
        label: 'Trình ký',
        description: 'Yêu cầu trình ký, quản lý trình ký, ký số.',
        actions: ['view', 'create', 'approve', 'config'],
        actionDescriptions: {
            create: 'Tạo yêu cầu trình ký.',
            approve: 'Ký duyệt / từ chối hồ sơ trình ký.',
            config: 'Tạo và sửa quy trình mẫu.',
        },
    },
    {
        key: 'asset',
        label: 'Tài sản',
        description: 'Danh sách tài sản, cấp phát, thu hồi.',
        actions: [...CRUD, 'config'],
        actionDescriptions: {
            create: 'Thêm tài sản, tạo phiếu cấp phát / thu hồi.',
            edit: 'Sửa tài sản, phiếu cấp phát / thu hồi.',
            config: 'Cài đặt tài sản.',
        },
    },
    {
        key: 'org',
        label: 'Sơ đồ & tiến trình',
        description: 'Sơ đồ tổ chức, tiến trình, quá trình làm việc.',
        actions: ['view', 'edit'],
    },

    // --- HRM ---
    {
        key: 'personnel',
        label: 'Nhân sự',
        description: 'Hồ sơ nhân sự, hợp đồng lao động, quyết định, lộ trình thăng tiến.',
        actions: [...CRUD, 'export'],
    },
    {
        key: 'recruitment',
        label: 'Tuyển dụng',
        description: 'Yêu cầu tuyển dụng, ứng viên, offer.',
        actions: [...CRUD, 'approve'],
        actionDescriptions: {
            approve: 'Duyệt yêu cầu tuyển dụng, duyệt offer ngoài khung lương.',
        },
    },
    {
        key: 'timekeeping',
        label: 'Chấm công',
        description: 'Chấm công, ngày nghỉ, phân ca, báo cáo chấm công.',
        actions: [...CRUD, 'assign', 'approve'],
        actionDescriptions: {
            create: 'Tạo đơn xin nghỉ, giải trình, làm thêm, đổi ca.',
            edit: 'Nhập dữ liệu máy chấm công, sửa quy định chấm công.',
            assign: 'Phân ca làm việc, sửa danh mục ca.',
            approve: 'Duyệt đơn từ, chốt / mở chốt công.',
        },
    },
    {
        key: 'salary',
        label: 'Bảng lương',
        description: 'Kỳ lương, tạm ứng, phiếu lương, thiết lập lương, thuế TNCN.',
        actions: ['view', 'edit', 'export', 'approve'],
        actionDescriptions: {
            edit: 'Tính lương, gửi duyệt, nhập tạm ứng / thưởng, sửa thiết lập lương.',
            approve: 'Duyệt bảng lương, xác nhận đã chi lương.',
        },
    },

    // --- Kho & Tài chính ---
    {
        key: 'warehouse',
        label: 'Kho hàng',
        description: 'Nhà cung cấp, hợp đồng mua, đơn mua, nhập / xuất / kiểm kho, báo cáo kho.',
        actions: [...CRUD, 'approve', 'export'],
    },
    {
        key: 'finance',
        label: 'Tài chính',
        description: 'Phiếu thu, phiếu chi, ngân sách, tài khoản ngân hàng, công nợ.',
        actions: [...CRUD, 'approve', 'export'],
    },
    {
        key: 'care',
        label: 'CSKH',
        description: 'Lịch chăm sóc khách hàng, tổng đài (Call center).',
        actions: ['view', 'create', 'edit', 'delete'],
        actionDescriptions: {
            create: 'Lưu khảo sát chăm sóc lần đầu, thêm ghi chú chăm sóc, tải ảnh điều trị lên.',
            edit: 'Sửa khảo sát chăm sóc và ghi chú chăm sóc đã lưu.',
            delete: 'Xóa ghi chú chăm sóc và ảnh điều trị.',
        },
    },
    { key: 'reception', label: 'Lễ tân', description: 'Check-in, hàng chờ và đặt lịch tại quầy lễ tân.', actions: ['view'] },
    { key: 'telesales', label: 'Telesales', description: 'Khách hàng, tổng đài, lịch hẹn, tư vấn và lịch làm việc của telesale.', actions: ['view'] },

    // --- Báo cáo ---
    { key: 'overview_report', label: 'Báo cáo tổng quan', description: 'Báo cáo KPI tổng hợp.', actions: ['view', 'export'] },
    { key: 'page_report', label: 'Báo cáo trang', description: 'Báo cáo theo fanpage / kênh.', actions: ['view', 'export'] },
    { key: 'sale_online_report', label: 'Báo cáo Sale Online', description: 'Báo cáo doanh số sale online.', actions: ['view', 'export'] },
    { key: 'customer_report', label: 'Báo cáo khách hàng', description: 'Báo cáo theo khách hàng.', actions: ['view', 'export'] },
    { key: 'truc_page', label: 'Dashboard kinh doanh', description: 'Dashboard tổng hợp Trực page · Telesale · Sale cơ sở: hành trình khách hàng, cảnh báo, hiệu suất nhân sự.', actions: ['view', 'export'] },
];

export interface PermissionGroup {
    key: string;
    label: string;
    moduleKeys: string[];
}

/** Gom nhóm module để hiển thị trên picker */
export const PERMISSION_GROUPS: PermissionGroup[] = [
    { key: 'general', label: 'Tổng quan', moduleKeys: ['overview'] },
    {
        key: 'cms',
        label: 'CMS / Nghiệp vụ',
        moduleKeys: ['chats', 'customer', 'appointment', 'consultation', 'execution', 'checkin', 'list_order', 'care', 'reception', 'telesales', 'marketing'],
    },
    {
        key: 'specialty',
        label: 'Chuyên môn',
        moduleKeys: [
            'hair_clinic',
            'miko_reception',
            'cosmetic_surgery',
            'surgery',
            'thread_lift',
            'ozempic',
            'beauty_consultation',
            'skin_consultation',
        ],
    },
    {
        key: 'categories',
        label: 'Danh mục',
        moduleKeys: ['sale_tags', 'chat_tags', 'profession', 'quick_messages'],
    },
    {
        key: 'employees',
        label: 'Nhân viên',
        moduleKeys: ['employee', 'roles', 'group_roles', 'departments'],
    },
    {
        key: 'catalog',
        label: 'Dịch vụ & Sản phẩm',
        moduleKeys: ['service_categories', 'services', 'product_categories', 'products'],
    },
    {
        key: 'workplace',
        label: 'Workplace',
        moduleKeys: ['social', 'task', 'project', 'signature', 'asset', 'org'],
    },
    { key: 'hrm', label: 'HRM', moduleKeys: ['personnel', 'recruitment', 'timekeeping', 'salary'] },
    { key: 'warehouse_finance', label: 'Kho & Tài chính', moduleKeys: ['warehouse', 'finance'] },
    { key: 'others', label: 'Khác', moduleKeys: ['advertise', 'branch_manager', 'pos', 'treatment', 'document'] },
    {
        key: 'reports',
        label: 'Báo cáo',
        moduleKeys: ['overview_report', 'page_report', 'sale_online_report', 'customer_report', 'truc_page'],
    },
];

// ---------- Helpers ----------

const MODULE_MAP: Record<string, PermissionModule> = PERMISSION_MODULES.reduce(
    (acc, m) => ({ ...acc, [m.key]: m }),
    {} as Record<string, PermissionModule>
);

export function getModule(moduleKey: string): PermissionModule | undefined {
    return MODULE_MAP[moduleKey];
}

/** Build permission key dạng `module.action` */
export function buildPermKey(moduleKey: string, action: PermissionActionKey): string {
    return `${moduleKey}.${action}`;
}

/** Tách permission key thành { module, action } */
export function parsePermKey(permKey: string): { moduleKey: string; action: PermissionActionKey } | null {
    const idx = permKey.indexOf('.');
    if (idx <= 0) return null;
    return {
        moduleKey: permKey.slice(0, idx),
        action: permKey.slice(idx + 1) as PermissionActionKey,
    };
}

/** Key có còn trong danh mục không (module tồn tại và có action đó). */
export function isValidPermKey(permKey: string): boolean {
    const parsed = parsePermKey(permKey);
    return !!parsed && !!getModule(parsed.moduleKey)?.actions.includes(parsed.action);
}

/**
 * Tách quyền của 1 nhóm thành key còn dùng và key cũ (module/action đã bị bỏ khỏi
 * danh mục, vd 'marketing.create'). Key cũ không còn tác dụng và được dọn khi lưu nhóm.
 */
export function splitPermissions(permissions: string[]): { valid: string[]; stale: string[] } {
    const valid: string[] = [];
    const stale: string[] = [];
    for (const k of permissions) (isValidPermKey(k) ? valid : stale).push(k);
    return { valid, stale };
}

/** Tổng số permission có thể chọn (dùng để hiển thị 12/345) */
export function getTotalPermissionCount(): number {
    return PERMISSION_MODULES.reduce((sum, m) => sum + m.actions.length, 0);
}

/**
 * Lấy mô tả hiển thị cho 1 (module, action) — ưu tiên override theo module,
 * fallback về mô tả mặc định của action. Trả về chuỗi rỗng nếu không có.
 */
export function getActionDescription(
    moduleKey: string,
    action: PermissionActionKey
): string {
    const m = getModule(moduleKey);
    const override = m?.actionDescriptions?.[action];
    if (override) return override;
    return PERMISSION_ACTIONS[action]?.description ?? '';
}
